import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CATEGORY_PRESETS, getTrackedCategoryOptions } from '../../pages/inspections/categoryOptions';
import '../../pages/monitoring/Monitoring.css';
import './FormBuilderModal.css';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Dropdown (one answer)' },
  { value: 'radio', label: 'Multiple choice (one answer)' },
  { value: 'checkbox', label: 'Checkboxes (multiple answers)' },
  { value: 'date', label: 'Date' },
];

const NEEDS_OPTIONS = ['select', 'radio', 'checkbox'];

const emptyQuestion = () => ({
  // Client-side only key for React/reordering; the backend assigns the
  // real question id used to key answers if this is left as a temp one.
  id: `tmp-${Math.random().toString(36).slice(2, 9)}`,
  type: 'text',
  label: '',
  required: false,
  options: [],
});

/**
 * Create or edit a PublicForm - the shareable, unauthenticated intake form
 * used for anonymous GBV reporting, stakeholder consultation feedback,
 * road safety tips, etc. Generic across every category rather than a
 * separate builder per category.
 */
const FormBuilderModal = ({
  isOpen, onClose, onSave, saving, projects, form = null, initialCategory = null,
  orgTrackingCategories = null, orgCustomCategories = [],
}) => {
  const isEdit = !!form;

  // Only offer categories this org actually tracks (plus its custom ones) -
  // see getTrackedCategoryOptions for the shared rules (null trackingCategories
  // means the org hasn't onboarded yet, so fall back to showing everything).
  const categoryOptions = useMemo(() => (
    getTrackedCategoryOptions(orgTrackingCategories, orgCustomCategories, form?.category || initialCategory)
  ), [orgTrackingCategories, orgCustomCategories, form, initialCategory]);

  const [title, setTitle] = useState(form?.title || '');
  const [description, setDescription] = useState(form?.description || '');
  const [category, setCategory] = useState(form?.category || initialCategory || categoryOptions[0]?.value || CATEGORY_PRESETS[0]);
  const [projectId, setProjectId] = useState(form?.projectId || '');
  const [questions, setQuestions] = useState(
    form?.questions?.length ? form.questions.map((q) => ({ ...q, options: q.options || [] })) : [emptyQuestion()]
  );
  // Pending "type one option, press Enter/Add" text per question, keyed by
  // question id - easier than typing a whole comma-separated string by hand.
  const [optionInputs, setOptionInputs] = useState({});

  const updateQuestion = (index, patch) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const addQuestion = () => setQuestions((prev) => [...prev, emptyQuestion()]);
  const removeQuestion = (index) => setQuestions((prev) => prev.filter((_, i) => i !== index));

  const addOption = (index, questionId) => {
    const value = (optionInputs[questionId] || '').trim();
    if (!value) return;
    setQuestions((prev) => prev.map((q, i) => {
      if (i !== index || q.options.includes(value)) return q;
      return { ...q, options: [...q.options, value] };
    }));
    setOptionInputs((prev) => ({ ...prev, [questionId]: '' }));
  };

  const removeOption = (index, value) => {
    updateQuestion(index, { options: questions[index].options.filter((o) => o !== value) });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please enter a title for this form');
      return;
    }
    if (questions.some((q) => !q.label.trim())) {
      toast.error('Every question needs a label');
      return;
    }
    if (questions.some((q) => NEEDS_OPTIONS.includes(q.type) && q.options.length === 0)) {
      toast.error('Dropdown/multiple-choice/checkbox questions need at least one option');
      return;
    }

    onSave({
      title: title.trim(),
      description: description.trim() || null,
      category,
      projectId: projectId || null,
      questions: questions.map((q) => ({
        // Only pass through a real (non-temp) id so the backend assigns a
        // stable one for brand-new questions.
        ...(q.id && !q.id.startsWith('tmp-') && { id: q.id }),
        type: q.type,
        label: q.label.trim(),
        required: q.required,
        ...(NEEDS_OPTIONS.includes(q.type) && { options: q.options }),
      })),
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="monitoring-modal form-builder-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-file-signature"></i> {isEdit ? 'Edit Form' : 'New Form'}</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-grid">
              <div className="form-group full-width">
                <label>Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Anonymous GBV/SEA Reporting Line"
                  required
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select value={category} onChange={(e) => setCategory(e.target.value)} required>
                  {categoryOptions.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Project</label>
                <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                  <option value="">Organisation-wide (all sites)</option>
                  {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  rows="2"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Shown to people filling in the form - explain what this is for and that it's anonymous, if applicable."
                />
              </div>
            </div>

            <div className="form-builder-questions">
              <h4>Questions</h4>
              {questions.map((question, index) => (
                <div key={question.id} className="form-builder-question">
                  <div className="form-builder-question-row">
                    <input
                      type="text"
                      className="form-builder-question-label"
                      value={question.label}
                      onChange={(e) => updateQuestion(index, { label: e.target.value })}
                      placeholder={`Question ${index + 1}`}
                    />
                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(index, { type: e.target.value })}
                    >
                      {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <label className="form-builder-required">
                      <input
                        type="checkbox"
                        checked={question.required}
                        onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    <button
                      type="button"
                      className="btn-icon danger"
                      title="Remove question"
                      onClick={() => removeQuestion(index)}
                      disabled={questions.length === 1}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                  {NEEDS_OPTIONS.includes(question.type) && (
                    <div className="form-builder-options">
                      <div className="form-builder-options-input-row">
                        <input
                          type="text"
                          value={optionInputs[question.id] || ''}
                          onChange={(e) => setOptionInputs((prev) => ({ ...prev, [question.id]: e.target.value }))}
                          placeholder="e.g. Yes"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addOption(index, question.id);
                            }
                          }}
                        />
                        <button type="button" className="btn-secondary-outline" onClick={() => addOption(index, question.id)}>
                          <i className="fas fa-plus"></i> Add
                        </button>
                      </div>
                      {question.options.length > 0 && (
                        <div className="form-builder-options-tags">
                          {question.options.map((option) => (
                            <span key={option} className="form-builder-option-tag">
                              {option}
                              <button type="button" onClick={() => removeOption(index, option)}>
                                <i className="fas fa-times"></i>
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button type="button" className="btn-secondary-outline" onClick={addQuestion}>
                <i className="fas fa-plus"></i> Add Question
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Form'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormBuilderModal;
